import { useEffect, useState } from 'react';
import {
    Create,
    SimpleForm,
    TextInput,
    SelectInput,
    useDataProvider,
    required,
    useNotify,
    BooleanInput,
    TopToolbar,
    Toolbar,
    Button,
    useRedirect,
    SaveButton
} from 'react-admin';
import { useFormContext, useWatch } from "react-hook-form"
import BackButton from '../../Components/BackButton';
import SaveIcon from '@mui/icons-material/Save'; // 新增保存图标
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'; // 新增下载图标
import { useNavigate } from 'react-router-dom';

const CONFIG_FIELDS = [
    'snPrefix',
    'accessIpAddress',
    'wifissid24Prefix',
    'wifissid50Prefix',
    'deviceName',
    'cmiitId',
    'productModel',
    'deviceBrand'
];

// 自定义验证函数
const validateOrderNo = (value: any) => {
    if (!/^[a-zA-Z0-9-_]+$/.test(value)) {
        return '只能包含字母、数字、下划线和中划线';
    }
    return undefined;
};

const validateBatchNo = (value: any) => {
    if (!/^[1-9]\d*$/.test(value)) {
        return '必须是正整数';
    }
    return undefined;
};

const validateAlphanumeric = (value: any) => {
    if (!/^[a-zA-Z0-9]+$/.test(value)) {
        return '只能包含字母和数字';
    }
    return undefined;
};

const validateIpAddress = (value: any) => {
    if (value && !/^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(value)) {
        return '必须是有效的IPv4地址';
    }
    return undefined;
};

const validateSsidPrefix = (value: any) => {
    if (value && !/^[a-zA-Z_]+$/.test(value)) {
        return '只能包含字母和下划线';
    }
    return undefined;
};

const validateDeviceName = (value: any) => {
    if (value && !/^[\u4e00-\u9fa5a-zA-Z0-9\s]+$/.test(value)) {
        return '只能包含字符';
    }
    return undefined;
};

const validateAlphanumericDotUnderscore = (value: any) => {
    if (value && !/^[a-zA-Z0-9:._]+$/.test(value)) {
        return '格式应为x.y.z';
    }
    return undefined;
};


const CustomToolbar = (props: any) => {
    const notify = useNotify();
    const form = useFormContext();
    const redirect = useRedirect();
    const navigate = useNavigate();

    const deviceType = useWatch({ name: 'deviceType' });

    useEffect(() => {
        if (deviceType) {
            // 从localStorage加载配置
            const config = JSON.parse(localStorage.getItem(`factoryTestingConfig_${deviceType}`));
            if (config) {
                CONFIG_FIELDS.forEach(field => {
                    form.setValue(field, config[field]);
                });
            }
        }
    }, [deviceType]);

    // 暂存表单数据
    const handleStash = () => {
        const formData = form.getValues();
        localStorage.setItem('factoryTestingCreateDraft', JSON.stringify(formData));
        notify('表单已暂存', { type: 'info' });
    };

    // 读取暂存数据
    const handleLoad = () => {
        const draft = localStorage.getItem('factoryTestingCreateDraft');
        if (draft) {
            const formData = JSON.parse(draft);
            Object.keys(formData).forEach(key => {
                form.setValue(key, formData[key]);
            });
            notify('已加载暂存的数据', { type: 'info' });
        } else {
            notify('没有找到暂存的数据', { type: 'warning' });
        }
    };

    // 保存并重定向
    const handleSave = () => {
        const formData = form.getValues();
        if (formData.deviceType) {
            const configToSave = {};
            CONFIG_FIELDS.forEach(field => {
                configToSave[field] = formData[field];
            });
            localStorage.setItem(`factoryTestingConfig_${formData.deviceType}`, JSON.stringify(configToSave));
        }
        redirect('list');
    };

    return (
        <>
            <Toolbar {...props} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                    <Button
                        label="暂存"
                        onClick={handleStash}
                        startIcon={<SaveIcon />}
                        sx={{ marginRight: '16px' }}
                    />
                    <Button
                        label="读取"
                        onClick={handleLoad}
                        startIcon={<CloudDownloadIcon />}
                    />
                </div>
                <div>
                    <Button
                        label="取消"
                        onClick={() => navigate(-1)}
                        sx={{ marginRight: '16px' }}
                    />
                    <SaveButton
                        label="保存"
                        icon={<></>}
                        onClick={handleSave}
                    />
                </div>
            </Toolbar>
        </>
    );
};

const ActionsToolbar = () => (
    <TopToolbar sx={{ justifyContent: 'start' }}>
        <BackButton />
    </TopToolbar>
);


export const factoryTestingCreate = () => {

    const dataProvider = useDataProvider();
    const [manufacturerTypes, setManufacturerTypes] = useState([]);
    const [deviceTypes, setDeviceTypes] = useState([]);
    const notify = useNotify();

    useEffect(() => {
        const fetchCustomerTypes = async () => {
            try {
                let types = await dataProvider.getManufacturerTypes();
                if (types) {
                    types = types.map((item: any) => ({
                        id: item.id,
                        name: item.name
                    }));
                } else {
                    types = []
                }

                setManufacturerTypes(types);
            } catch (error) {
                console.error("获取客户类型失败:", error);
                notify("获取制造商列表失败", { type: 'error' });
            }
        };

        const fetchDeviceTypes = async () => {
            try {
                let types = await dataProvider.getDeviceList();
                types = types.map((item: any) => ({
                    id: item.name,
                    name: item.name
                }));

                setDeviceTypes(types);
            } catch (error) {
                console.error("获取设备类型失败:", error);
            }
        };

        fetchDeviceTypes();
        fetchCustomerTypes();
    }, []);

    return (
        <Create actions={<ActionsToolbar />} redirect="list" >
            <SimpleForm sx={{ width: { lg: '40%', xs: '100%' } }} toolbar={<CustomToolbar />} warnWhenUnsavedChanges>
                <SelectInput
                    label="设备类型"
                    source="deviceType"
                    choices={deviceTypes}
                    helperText="设备类型：根据产品硬件形态定义"
                    validate={[required()]}
                />
                <TextInput
                    label="设备名称"
                    source="deviceName"
                    validate={[validateDeviceName]}
                />

                <TextInput
                    label="产品型号"
                    source="productModel"
                    helperText="示例：HY3000"
                    validate={[required(), validateAlphanumeric]}
                />

                <TextInput
                    label="产品品牌"
                    source="deviceBrand"
                    helperText="产品品牌英文名称"
                    validate={[validateAlphanumeric]}
                />

                <SelectInput
                    alwaysOn
                    source="manufacturerId"
                    label="制造工厂"
                    choices={manufacturerTypes}
                    validate={[required()]}
                />

                <TextInput
                    label="订单号"
                    source="orderNo"
                    helperText="订单号：给工厂下单的订单号"
                    validate={[required(), validateOrderNo]}
                />

                <TextInput
                    label="批次号"
                    source="batchNo"
                    helperText="表示当前订单号的生产批次"
                    validate={[required(), validateBatchNo]}
                />

                <TextInput
                    label="订单数量"
                    source="orderCount"
                    helperText="该批次的数量"
                    validate={[required(), validateBatchNo]}
                />
                <TextInput
                    label="设备MAC用量"
                    source="macPerSn"
                    helperText="设备MAC用量：请与开发人员确认设备MAC用量"
                    validate={[required(), validateBatchNo]}
                />
                <TextInput
                    label="SN码前缀"
                    source="snPrefix"
                    helperText="示例：品牌代码2位（PH表示飞利浦）+备选2位（默认00） +硬件类型1位（表芯片方案：M表示MTK，H表示海思）+代工厂1位（A表示智路由，B表示意拓智能，C表富士康）"
                    validate={[required(), validateAlphanumeric]}
                />

                <TextInput
                    label="管理地址"
                    source="accessIpAddress"
                    validate={[validateIpAddress]}
                />

                <TextInput
                    label="2.4G wifi ssid"
                    source="wifissid24Prefix"
                    helperText="2.4G wifi SSID：仅填写前缀PHILIPS，系统自动添加MAC地址后4位生成数据; 自动生成示例：PHILIPS_BD80"
                    validate={[validateSsidPrefix]}
                />

                <TextInput
                    label="5G wifi ssid"
                    source="wifissid50Prefix"
                    helperText="5G wifi SSID：仅填写前缀PHILIPS，系统自动添加MAC地址后4位生成数据; 自动生成示例：PHILIPS_BD80_5G"
                    sx={{
                        '& .MuiFormHelperText-root': {
                            whiteSpace: 'nowrap'
                        }
                    }}
                    validate={[validateSsidPrefix]}
                />

                <TextInput
                    label="软件版本"
                    source="softVersion"
                    validate={[validateAlphanumericDotUnderscore]}
                />

                <TextInput
                    label="硬件版本"
                    source="hwVersion"
                    validate={[validateAlphanumericDotUnderscore]}
                />
                <TextInput
                    label="固件版本"
                    source="firmware"
                    validate={[validateAlphanumericDotUnderscore]}
                />

                <TextInput
                    label="SRRC证书号"
                    source="cmiitId"
                    helperText="SRRC证书号：为无委证书编码"
                    validate={[validateAlphanumeric]}
                />
                <BooleanInput
                    label="生成 SSIDKEY"
                    source="generateSsidPassword"
                />
                <BooleanInput
                    label="生成 AccessPassword"
                    source="generateAccessPassword"
                />
            </SimpleForm>
        </Create>
    );
}